import {
	type CSSProperties,
	type EmitFn,
	computed,
	onBeforeUnmount,
	onMounted,
	ref,
	watch
} from 'vue'

export type SnackbarColor =
	| 'success'
	| 'error'
	| 'warning'
	| 'inform'
	| 'neutral'

export type SnackbarPosition =
	| 'top-start'
	| 'top-center'
	| 'top-end'
	| 'bottom-start'
	| 'bottom-center'
	| 'bottom-end'

export interface BSnackbarProps {
	/** Applies a distinct style to the component */
	advanced?: boolean
	/** Specify content title */
	title?: string
	/** Specify content description - for advanced variant */
	description?: string
	/** Specify the color of the component */
	color?: SnackbarColor
	/** Adds close button then component is closable */
	closable?: boolean
	/** Change the default icon used for close snackbar, using Hugeicons set */
	closeIcon?: string
	/** Adds progress bar */
	hasProgress?: boolean
	/** Specify the duration of the snackbar in milliseconds */
	duration?: number
	/** Specify the position of the snackbar */
	position?: SnackbarPosition
	/** Specify the action button text */
	actionText?: string
	/** Specify the snackbar type - defaults: success, error, warning, info - for advanced variant */
	icon?: string | boolean
	/** Controls the visibility of the snackbar */
	modelValue?: boolean
	/** Teleport the snackbar to a different DOM element */
	teleportTo?: string
}

export interface BSnackbarEvents {
	/** Emitted when close icon is clicked */
	'click:close'?: [event: MouseEvent]
	/** Emitted when action button is clicked */
	'click:action'?: [event: MouseEvent]
	/** Emitted when snackbar leave */
	'after-leave'?: []
	/** Update model value */
	'update:modelValue'?: [value: boolean]
}

export interface BSnackbarSlots {
	/** Default slot content - overrides title and description prop */
	default: void
	/** Action slot content - overrides actionText prop */
	action: void
	/** Custom close icon content - overrides closeIcon prop */
	'close-icon': void
}

const COLOR_ICON_MAP: Record<SnackbarColor, string> = {
	success: 'checkmark-circle-02',
	error: 'cancel-circle',
	warning: 'alert-circle',
	inform: 'information-circle',
	neutral: 'information-circle'
}

const POSITION_STYLES: Record<SnackbarPosition, CSSProperties> = {
	'top-start': {
		top: '2.5rem',
		insetInlineStart: '2.5rem'
	},
	'top-center': {
		top: '2.5rem',
		left: '50%',
		transform: 'translateX(-50%)'
	},
	'top-end': {
		top: '2.5rem',
		insetInlineEnd: '2.5rem'
	},
	'bottom-start': {
		bottom: '2.5rem',
		insetInlineStart: '2.5rem'
	},
	'bottom-center': {
		bottom: '2.5rem',
		left: '50%',
		transform: 'translateX(-50%)'
	},
	'bottom-end': {
		bottom: '2.5rem',
		insetInlineEnd: '2.5rem'
	}
}

export function useSnackbar(
	props: BSnackbarProps,
	emits: EmitFn<BSnackbarEvents>
) {
	const progressValue = ref(0)
	let progressInterval: number | undefined
	const isHovered = ref(false)
	const screenWidth = ref(window.innerWidth)

	const updateScreenWidth = () => {
		screenWidth.value = window.innerWidth
	}

	const closeSnackbar = () => {
		emits('update:modelValue', false)
	}

	const handleClose = (event: MouseEvent) => {
		emits('click:close', event)
		closeSnackbar()
	}

	const handleAction = (event: MouseEvent) => {
		emits('click:action', event)
		closeSnackbar()
	}

	const handleMouseEnter = () => {
		isHovered.value = true
		if (progressInterval) {
			clearInterval(progressInterval)
			progressInterval = undefined
		}
	}

	const handleMouseLeave = () => {
		isHovered.value = false
		if (props.hasProgress && props.duration && props.modelValue) {
			startProgressTimer(true)
		}
	}

	const classes = computed(() => {
		const baseClasses = [
			'rounded-md',
			'inline-flex',
			'items-center',
			'px-4',
			'py-2',
			'max-w-full',
			'z-150',
			'relative',
			'overflow-hidden'
		]
		if (!props.advanced) {
			baseClasses.push('border', 'text-body-1')
			if (props.hasProgress) {
				baseClasses.push('border-b-0')
			} else {
				baseClasses.push('border-b-3')
			}
		} else {
			baseClasses.push('border-s-6', 'text-body-2')
		}
		return baseClasses.join(' ')
	})

	const getPositionStyles = computed(() => {
		const position = props.position || 'bottom-start'
		if (screenWidth.value < 480) {
			const isTop = position.startsWith('top')
			return {
				[isTop ? 'top' : 'bottom']: '1rem',
				left: '50%',
				transform: 'translateX(-50%)',
				width: 'calc(100% - 2rem)',
				maxWidth: '100%'
			}
		}
		return POSITION_STYLES[position]
	})

	const styles = computed(() => {
		const style: CSSProperties = {
			position: 'fixed',
			...getPositionStyles.value
		}

		if (props.color === 'neutral') {
			style.backgroundColor = `var(--color-background-lowest)`
			style.color = `var(--color-on-surface-variant)`
			style.borderColor = `var(--color-surface-variant-2)`
		} else {
			style.backgroundColor = `var(--color-${props.color}-container)`
			style.color = !props.advanced
				? `var(--color-on-${props.color}-container)`
				: `var(--color-on-surface)`
			style.borderColor = !props.advanced
				? `var(--color-on-${props.color}-container)`
				: `var(--color-${props.color})`
		}

		return style
	})

	const iconName = computed(() => {
		if (typeof props.icon === 'string') {
			return props.icon
		}
		if ((props.icon === true || props.icon === undefined) && props.color) {
			return COLOR_ICON_MAP[props.color]
		}
		return ''
	})

	const startProgressTimer = (resume = false) => {
		if (!props.duration) {
			return
		}

		clearInterval(progressInterval)

		if (!resume) {
			progressValue.value = 0
		}

		const steps = 100
		const intervalTime = props.duration / steps
		const remainingSteps = 100 - progressValue.value
		const remainingTime = (props.duration * remainingSteps) / 100

		progressInterval = window.setInterval(
			() => {
				progressValue.value += 1
				if (progressValue.value >= 100) {
					clearInterval(progressInterval)
					closeSnackbar()
				}
			},
			resume ? remainingTime / remainingSteps : intervalTime
		)
	}

	watch(
		() => [props.duration, props.hasProgress],
		() => {
			if (!isHovered.value) {
				startProgressTimer()
			}
		}
	)

	watch(
		() => props.modelValue,
		(newValue) => {
			if (newValue === true && !isHovered.value) {
				startProgressTimer()
			} else if (progressInterval) {
				clearInterval(progressInterval)
			}
		}
	)

	const isVisible = ref(false)

	watch(
		() => props.modelValue,
		(newValue) => {
			if (newValue) {
				isVisible.value = true
			} else {
				setTimeout(() => {
					isVisible.value = false
				}, 300)
			}
		},
		{ immediate: true }
	)

	onMounted(() => {
		window.addEventListener('resize', updateScreenWidth)
		isVisible.value = !!props.modelValue
		if (props.modelValue !== false && !isHovered.value) {
			startProgressTimer()
		}
	})

	onBeforeUnmount(() => {
		window.removeEventListener('resize', updateScreenWidth)
		if (progressInterval) {
			clearInterval(progressInterval)
		}
	})

	const computedProgress = computed(() => progressValue.value)
	const showDescription = computed(() => props.advanced && props.description)

	return {
		isVisible,
		showDescription,
		computedProgress,
		iconName,
		classes,
		styles,
		handleClose,
		handleAction,
		closeSnackbar,
		handleMouseEnter,
		handleMouseLeave
	}
}
